import { useState } from "react";
import { ChevronDown } from "lucide-react";
import { PRICE_BOUNDS } from "../../lib/filterTaxonomy";

// Reusable custom checkbox — never renders a native checkbox (spec requirement).
function CheckRow({ checked, label, count, onToggle, testId }) {
  return (
    <button
      type="button"
      onClick={onToggle}
      className="w-full flex items-center gap-3 py-1.5 group text-left"
      data-testid={testId}
    >
      <span
        aria-hidden
        className={
          "w-4 h-4 shrink-0 border transition-colors flex items-center justify-center " +
          (checked ? "bg-[#FF9F1C] border-[#FF9F1C]" : "bg-transparent border-white/40 group-hover:border-white")
        }
      >
        {checked && (
          <svg viewBox="0 0 12 12" className="w-3 h-3" aria-hidden>
            <path d="M2 6.5 5 9.5 10 3" stroke="#0A0A0A" strokeWidth="2" fill="none" strokeLinecap="round" strokeLinejoin="round"/>
          </svg>
        )}
      </span>
      <span className="flex-1 font-sans text-[13px] text-white group-hover:text-[#FF9F1C] transition-colors uppercase">
        {label}
      </span>
      {typeof count === "number" && (
        <span className="font-mono text-[11px] text-[#6b6b6b] tabular-nums">{count}</span>
      )}
    </button>
  );
}

function Section({ label, open, onToggle, children }) {
  return (
    <div className="border-b border-[#1F1F1F]">
      <button
        type="button"
        onClick={onToggle}
        className="w-full flex items-center justify-between py-4 font-mono text-[13px] uppercase tracking-[0.08em] text-white hover:text-[#FF9F1C] transition-colors"
      >
        <span>{label}</span>
        <ChevronDown className={"h-4 w-4 transition-transform " + (open ? "" : "-rotate-90")} />
      </button>
      {open && <div className="pb-4">{children}</div>}
    </div>
  );
}

function PriceRange({ value, onChange, bounds }) {
  const min = value?.min != null ? value.min : bounds.min;
  const max = value?.max != null ? value.max : bounds.max;

  const setMin = (n) => onChange({ min: Math.max(bounds.min, Math.min(n, max)), max });
  const setMax = (n) => onChange({ min, max: Math.min(bounds.max, Math.max(n, min)) });

  const minPct = ((min - bounds.min) / (bounds.max - bounds.min)) * 100;
  const maxPct = ((max - bounds.min) / (bounds.max - bounds.min)) * 100;

  return (
    <div className="space-y-3">
      <div className="relative h-6 flex items-center px-[6px]">
        <div className="absolute left-[6px] right-[6px] h-[2px] bg-[#1F1F1F]" />
        <div
          className="absolute h-[2px] bg-[#FF9F1C]"
          style={{ left: `calc(${minPct}% * (100% - 12px) / 100% + 6px)`, right: `calc(${100 - maxPct}% * (100% - 12px) / 100% + 6px)` }}
        />
        <input
          type="range"
          min={bounds.min}
          max={bounds.max}
          step={bounds.step}
          value={min}
          onChange={(e) => setMin(Number(e.target.value))}
          className="absolute inset-0 w-full appearance-none bg-transparent pointer-events-auto opacity-0"
          aria-label="Minimum price"
        />
        <input
          type="range"
          min={bounds.min}
          max={bounds.max}
          step={bounds.step}
          value={max}
          onChange={(e) => setMax(Number(e.target.value))}
          className="absolute inset-0 w-full appearance-none bg-transparent pointer-events-auto opacity-0"
          aria-label="Maximum price"
        />
        {/* Visible thumbs — inset so 0% and 100% sit inside the track */}
        <span
          aria-hidden
          className="absolute w-3 h-3 -mt-[5px] rounded-full bg-[#FF9F1C] border border-[#0A0A0A]"
          style={{ left: `calc(${minPct}% * (100% - 12px) / 100%)` }}
        />
        <span
          aria-hidden
          className="absolute w-3 h-3 -mt-[5px] rounded-full bg-[#FF9F1C] border border-[#0A0A0A]"
          style={{ left: `calc(${maxPct}% * (100% - 12px) / 100%)` }}
        />
      </div>
      <div className="flex items-center gap-2 text-white">
        <label className="flex items-center gap-1 bg-[#0A0A0A] border border-[#1F1F1F] px-2 py-1 flex-1">
          <span className="font-mono text-[11px] text-[#8A8A8A]">₹</span>
          <input
            type="number"
            value={min}
            onChange={(e) => setMin(Number(e.target.value || 0))}
            className="w-full bg-transparent outline-none font-mono text-[12px] tabular-nums"
          />
        </label>
        <span className="font-mono text-[11px] text-[#8A8A8A]">—</span>
        <label className="flex items-center gap-1 bg-[#0A0A0A] border border-[#1F1F1F] px-2 py-1 flex-1">
          <span className="font-mono text-[11px] text-[#8A8A8A]">₹</span>
          <input
            type="number"
            value={max}
            onChange={(e) => setMax(Number(e.target.value || 0))}
            className="w-full bg-transparent outline-none font-mono text-[12px] tabular-nums"
          />
        </label>
      </div>
    </div>
  );
}

export default function FilterSidebar({
  taxonomy,
  facets,
  active,
  onToggleValue,
  onSetPrice,
  className = "",
}) {
  // Initial open sections: brand + price + stock.
  const [openSet, setOpenSet] = useState(() => new Set(Object.keys(taxonomy)));
  const toggle = (k) => {
    setOpenSet((prev) => {
      const next = new Set(prev);
      if (next.has(k)) next.delete(k);
      else next.add(k);
      return next;
    });
  };

  const rendered = Object.entries(taxonomy).filter(([key, dim]) => {
    if (dim.type === "price") return true;
    const f = facets[key];
    if (!f) return false;
    if (dim.type === "stock") return (f.values || []).length > 0;
    return (f.values || []).length > 0;
  });

  return (
    <aside className={"w-[240px] shrink-0 border-r border-[#1F1F1F] pr-4 " + className}>
      <div className="sticky top-[88px] max-h-[calc(100vh-88px)] overflow-y-auto pr-2 pb-4">
        {rendered.map(([key, dim]) => (
          <Section
            key={key}
            label={dim.label}
            open={openSet.has(key)}
            onToggle={() => toggle(key)}
          >
            {dim.type === "price" ? (
              <PriceRange
                value={active[key]}
                onChange={(v) => onSetPrice(key, v)}
                bounds={{
                  min: facets[key]?.min ?? PRICE_BOUNDS.min,
                  max: facets[key]?.max ?? PRICE_BOUNDS.max,
                  step: PRICE_BOUNDS.step,
                }}
              />
            ) : (
              (facets[key]?.values || []).map((v) => {
                const set = active[key] instanceof Set ? active[key] : null;
                const checked = !!set && set.has(v.key);
                return (
                  <CheckRow
                    key={v.key}
                    checked={checked}
                    label={v.label}
                    count={v.count}
                    onToggle={() => onToggleValue(key, v.key)}
                  />
                );
              })
            )}
          </Section>
        ))}
      </div>
    </aside>
  );
}
